import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import CategorySlide from "../Slider/CategorySlide";
import RestaurantSlide from "../Slider/RestaurantSlide";
import downloadbg from "../../assets/images/download-bg.png";
import serviceicon01 from "../../assets/images/service-icon-01.png";
import serviceicon02 from "../../assets/images/service-icon-02.png";
import serviceicon03 from "../../assets/images/service-icon-03.png";
import serviceicon04 from "../../assets/images/service-icon-04.png";
import chooseimg01 from "../../assets/images/choose-img.png";
import yearicon from "../../assets/images/year-icon.png";
import chooseimg02 from "../../assets/images/choose-img-02.png";
import { useDispatch, useSelector } from "react-redux";
import toastify from "../../helper/toastify";
import { fynitoApi } from "../Services/axios";
import apiCache from "../../services/apiCache";
import {
  loadingStart,
  loadingStop,
} from "../../redux/features/loading/loadingSlice";
import { Link } from "react-router-dom";
import TrackingBar from "../Order/TrackingBar";
import MobileBottomBar from "../Layout/MobileBottomBar";
import { filterRestaurantOffers } from "../../utils/couponUtils";

function Home() {
  const dispatch = useDispatch();
  const { location } = useSelector((state) => state.location);
  const { isLogin, user } = useSelector((state) => state.auth);
  
  // Debug: Log user data to see the structure
  const [homeData, setHomeData] = useState({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [filters, setFilters] = useState({
    sortBy: "relevance", // relevance | delivery_time | rating_desc
    pureVeg: false,
    fastDelivery: false,
    offers: false,
    isNew: false,
    ratingGteFour: false,
  });

  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  
  // Refs to prevent duplicate API calls
  const fetchingHomeDataRef = useRef(false);
  const lastLocationRef = useRef(null);

  useEffect(() => {
    const storedOrderId = localStorage.getItem("latestOrderId");
    const storedStatus = localStorage.getItem("latestOrderStatus");

    setOrderId(storedOrderId);
    setOrderStatus(storedStatus);
  }, []);

  // Scroll to top is now handled globally in Layout component


  // Resolve effective location: Redux + localStorage fallback (same source as Header)
  const getEffectiveLocation = useCallback(() => {
    const fromRedux = location?.latitude != null && location?.longitude != null
      ? { latitude: location.latitude, longitude: location.longitude }
      : null;
    if (fromRedux) return fromRedux;
    try {
      const stored = localStorage.getItem("userLocation");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.latitude != null && parsed?.longitude != null) {
          return { latitude: parsed.latitude, longitude: parsed.longitude };
        }
      }
    } catch (e) {}
    return null;
  }, [location?.latitude, location?.longitude]);

  // Fetch Home Data API: uses query params so server receives lat/long (GET body is often ignored)
  const fetchHomeData = useCallback(async (customLocation = null) => {
    const locationToUse = customLocation || getEffectiveLocation();
    const defaultLat = 26.8828672;
    const defaultLng = 75.7628928;
    const lat = locationToUse?.latitude != null ? Number(locationToUse.latitude) : defaultLat;
    const lng = locationToUse?.longitude != null ? Number(locationToUse.longitude) : defaultLng;
    const locationKey = `${lat}_${lng}`;

    if (fetchingHomeDataRef.current || lastLocationRef.current === locationKey) {
      return;
    }

    fetchingHomeDataRef.current = true;
    lastLocationRef.current = locationKey;

    try {
      setIsDataLoading(true);
      dispatch(loadingStart());

      const params = {
        search_text: "",
        rating: "",
        is_veg: "",
        user_current_lat: String(lat),
        user_current_long: String(lng),
      };

      const cacheKey = apiCache.getCacheKey("/home-page-for-web", "GET", params, {});
      const cachedData = apiCache.get(cacheKey, 120000);

      if (cachedData) {
        const filteredCachedData = {
          ...cachedData,
          Restaurant: cachedData.Restaurant ? {
            ...cachedData.Restaurant,
            data: filterRestaurantOffers(cachedData.Restaurant.data || []),
          } : cachedData.Restaurant,
        };
        setHomeData(filteredCachedData);
        setIsDataLoading(false);
        dispatch(loadingStop());
        fetchingHomeDataRef.current = false;
        return;
      }

      if (apiCache.isPending(cacheKey)) {
        const pendingResponse = await apiCache.getPending(cacheKey);
        if (pendingResponse?.data?.status_code === 200) {
          const data = pendingResponse.data.data;
          const filteredData = {
            ...data,
            Restaurant: data.Restaurant ? {
              ...data.Restaurant,
              data: filterRestaurantOffers(data.Restaurant.data || []),
            } : data.Restaurant,
          };
          setHomeData(filteredData);
          apiCache.set(cacheKey, filteredData);
        }
        setIsDataLoading(false);
        dispatch(loadingStop());
        fetchingHomeDataRef.current = false;
        return;
      }

      const requestPromise = fynitoApi.get("/home-page-for-web", { params });
      apiCache.setPending(cacheKey, requestPromise);

      const res = await requestPromise;

      if (res.data.status_code === 200) {
        const data = res.data.data;
        const filteredData = {
          ...data,
          Restaurant: data.Restaurant ? {
            ...data.Restaurant,
            data: filterRestaurantOffers(data.Restaurant.data || []),
          } : data.Restaurant,
        };
        setHomeData(filteredData);
        apiCache.set(cacheKey, filteredData);
      } else {
        toastify.error(res.data.message || "Failed to load home data");
      }
    } catch (error) {
      const is401Error =
        error.response?.status === 401 ||
        error.status === 401 ||
        error._isHandled401 ||
        error.isHandled;
      if (is401Error) return;
      console.error("Error fetching home data:", error);
    } finally {
      setIsDataLoading(false);
      dispatch(loadingStop());
      fetchingHomeDataRef.current = false;
    }
  }, [location, getEffectiveLocation, dispatch]);

  // Initial data fetch when component mounts (uses effective location from Redux or localStorage)
  useEffect(() => {
    fetchHomeData();
  }, [fetchHomeData]);

  // Refetch home data when user logs in
  useEffect(() => {
    if (isLogin) {
      fetchingHomeDataRef.current = false;
      lastLocationRef.current = null;
      fetchHomeData();
    }
  }, [isLogin, fetchHomeData]);

  // When location changes: reset refs and refetch immediately so API gets new lat/long
  useEffect(() => {
    const effective = getEffectiveLocation();
    if (effective?.latitude == null || effective?.longitude == null) return;
    const newKey = `${effective.latitude}_${effective.longitude}`;
    if (newKey === lastLocationRef.current) return;
    lastLocationRef.current = null;
    fetchingHomeDataRef.current = false;
    fetchHomeData(effective);
  }, [location?.latitude, location?.longitude, getEffectiveLocation, fetchHomeData]);

  // Set loading to false if no data after initial load and not currently fetching
  useEffect(() => {
    if (!isDataLoading && Object.keys(homeData).length === 0) {
      setIsDataLoading(false);
    }
  }, [homeData, isDataLoading]);

  const parseDeliveryMinutes = (deliveryTime) => {
    if (!deliveryTime) return Number.POSITIVE_INFINITY;
    const numeric = parseInt(String(deliveryTime).toString().match(/\d+/)?.[0] || "");
    if (Number.isNaN(numeric)) return Number.POSITIVE_INFINITY;
    return numeric;
  };

  const topRestaurantsFiltered = useMemo(() => {
    const list = Array.isArray(homeData?.Restaurant?.data) ? homeData.Restaurant.data : [];

    let filtered = list.filter((rest) => {
      if (filters.pureVeg && String(rest?.is_veg) !== "1") return false;
      // Check offers after filtering expired ones
      const validOffers = rest?.offer ? filterRestaurantOffers(rest).offer : [];
      if (filters.offers && !(Array.isArray(validOffers) && validOffers.length > 0)) return false;
      if (filters.isNew && !rest?.is_new) return false;
      if (filters.ratingGteFour && Number(rest?.rating || 0) < 4) return false;
      if (filters.fastDelivery && parseDeliveryMinutes(rest?.delivery_time) > 30) return false;
      return true;
    });

    // Sorting
    if (filters.sortBy === "delivery_time") {
      filtered = [...filtered].sort((a, b) => parseDeliveryMinutes(a?.delivery_time) - parseDeliveryMinutes(b?.delivery_time));
    } else if (filters.sortBy === "rating_desc") {
      filtered = [...filtered].sort((a, b) => Number(b?.rating || 0) - Number(a?.rating || 0));
    }

    // Filter expired coupons from filtered restaurants
    return filtered.map(rest => filterRestaurantOffers(rest));
  }, [homeData?.Restaurant?.data, filters]);

  const appliedFiltersCount = useMemo(() => {
    return [filters.pureVeg, filters.fastDelivery, filters.offers, filters.isNew, filters.ratingGteFour].filter(Boolean).length;
  }, [filters]);

  const clearAllFilters = () => {
    setFilters({
      sortBy: "relevance",
      pureVeg: false,
      fastDelivery: false,
      offers: false,
      isNew: false,
      ratingGteFour: false,
    });
  };
  return (
    <>
      <section className="category-sec tp-space pt-100">
        <div className="container">
                    <h3 className="title" style={{ width: '70%', maxWidth: '70%' }}>
                      {isLogin && user ? (
                        user.firstname ? `${user.firstname}, ` :
                        user.first_name ? `${user.first_name}, ` :
                        user.name ? `${user.name}, ` :
                        user.user_name ? `${user.user_name}, ` : ''
                      ) : ''}What's on your mind?
                    </h3>
                    {isDataLoading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-danger" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading categories...</p>
                      </div>
                    ) : homeData?.category ? (
                      <CategorySlide category={homeData?.category} />
                    ) : (
                      <p className="text-center text-muted">No categories available</p>
                    )}
                </div>
      </section>

      <section className="restaurant-sec tp-space pt-0">
        <div className="container">
          {/* Horizontal divider */}
          <hr className="section-divider" style={{ margin: '24px 0', border: 'none', height: '1px', backgroundColor: '#e0e0e0' }} />
          
          <div className="mb-4">
            <h3 className="title mb-0">Top restaurants near you</h3>
          </div>
          {isLogin && (
            <div className="d-flex align-items-center gap-2 filters-block mt-2 mb-4">
                <button
                  type="button"
                  className={`btn btn-outline-danger d-flex align-items-center gap-2 ${appliedFiltersCount ? 'position-relative' : ''}`}
                  data-bs-toggle="modal"
                  data-bs-target="#restaurantFiltersModal"
                  onClick={() => setIsFilterModalOpen(true)}
                >
                  <i className="fas fa-sliders" />
                  Filters
                  {appliedFiltersCount > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {appliedFiltersCount}
                    </span>
                  )}
                </button>
                <div className="btn-group" role="group" aria-label="Sort options">
                  <button
                    type="button"
                    className={`btn ${filters.sortBy === 'relevance' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilters((p) => ({ ...p, sortBy: 'relevance' }))}
                  >
                    Relevance
                  </button>
                  <button
                    type="button"
                    className={`btn ${filters.sortBy === 'delivery_time' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilters((p) => ({ ...p, sortBy: 'delivery_time' }))}
                  >
                    Time
                  </button>
                  <button
                    type="button"
                    className={`btn ${filters.sortBy === 'rating_desc' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilters((p) => ({ ...p, sortBy: 'rating_desc' }))}
                  >
                    Rating
                  </button>
                </div>
            </div>
          )}
          {isDataLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Finding restaurants near you...</p>
            </div>
          ) : homeData?.Restaurant?.data?.length > 0 ? (
            <RestaurantSlide restaurant={topRestaurantsFiltered} categories={homeData?.category} />
          ) : (
            <p className="text-center text-muted">No restaurants found in this area</p>
          )}
        </div>
      </section>

      <section className="restaurant-sec tp-space pb-5 pt-0">
        <div className="container">
          {/* Horizontal divider */}
          <hr className="section-divider" style={{ margin: '24px 0', border: 'none', height: '1px', backgroundColor: '#e0e0e0' }} />
          
          {/* <h3 className="title">Near By Restaurants</h3> */}
          <h3 className="title">Suggested Restaurants</h3>

          {isDataLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Finding restaurants near you...</p>
            </div>
          ) : homeData?.Restaurant?.data?.length > 0 ? (
            <RestaurantSlide restaurant={filterRestaurantOffers(homeData?.Restaurant?.data || [])} categories={homeData?.category} />
          ) : (
            <p className="text-center text-muted">No restaurants found in this area</p>
          )}
        </div>
      </section>

      {/* Filters Modal - Centered with cancel icon */}
      <div
        className="modal fade filters-modal"
        id="restaurantFiltersModal"
        tabIndex="-1"
        aria-labelledby="restaurantFiltersModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <h5 className="modal-title mb-0" id="restaurantFiltersModalLabel">Filters & Sort</h5>
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => setIsFilterModalOpen(false)}
              >
                <i className="fa-solid fa-xmark fs-4"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <h6 className="section-label">Quick filters</h6>
                <div className="chips-wrap">
                  <button
                    type="button"
                    className={`filter-chip ${filters.pureVeg ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, pureVeg: !p.pureVeg }))}
                  >
                    <i className="fas fa-leaf" />
                    <span>Pure Veg</span>
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.fastDelivery ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, fastDelivery: !p.fastDelivery }))}
                  >
                    <i className="fas fa-bolt" />
                    <span>Fast Delivery</span>
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.offers ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, offers: !p.offers }))}
                  >
                    <i className="fas fa-badge-percent" />
                    <span>Offers</span>
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.isNew ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, isNew: !p.isNew }))}
                  >
                    <i className="fas fa-star" />
                    <span>New on Fynito</span>
                  </button>
                  <button
                    type="button"
                    className={`filter-chip ${filters.ratingGteFour ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, ratingGteFour: !p.ratingGteFour }))}
                  >
                    <i className="fas fa-star" />
                    <span>Rating 4.0+</span>
                  </button>
                </div>
              </div>

              <div className="mb-2">
                <h6 className="section-label">Sort by</h6>
                <div className="segmented">
                  <button
                    type="button"
                    className={`segment ${filters.sortBy === 'relevance' ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, sortBy: 'relevance' }))}
                  >
                    Relevance
                  </button>
                  <button
                    type="button"
                    className={`segment ${filters.sortBy === 'delivery_time' ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, sortBy: 'delivery_time' }))}
                  >
                    Delivery Time
                  </button>
                  <button
                    type="button"
                    className={`segment ${filters.sortBy === 'rating_desc' ? 'active' : ''}`}
                    onClick={() => setFilters((p) => ({ ...p, sortBy: 'rating_desc' }))}
                  >
                    Rating
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between">
              <button type="button" className="btn btn-outline-secondary" onClick={clearAllFilters}>
                Clear All
              </button>
              <button
                type="button"
                className="btn btn-danger"
                data-bs-dismiss="modal"
                onClick={() => setIsFilterModalOpen(false)}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="download-bottom">
        <img src={downloadbg} alt="downloadbg" />
      </div>
      <section className="service-sec tp-space">
        <div className="container">
          <div className="row">
            <div className="col-lg-3 col-md-6 col-sm-6">
              <div className="service-card">
                <span>
                  <img src={serviceicon01} alt="serviceicon01" />
                </span>
                <h5>Best Quality Food</h5>
                <p>
                  Excellence projecting is devonshire dispatched remarkably on
                  estimating. Side in so life past.
                </p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <div className="service-card">
                <h5>Home delivery</h5>
                <p>
                  Excellence projecting is devonshire dispatched remarkably on
                  estimating. Side in so life past.
                </p>
                <span>
                  <img src={serviceicon02} alt="serviceicon02" />
                </span>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <div className="service-card">
                <span>
                  <img src={serviceicon03} alt="serviceicon03" />
                </span>
                <h5>Real Taste</h5>
                <p>
                  Excellence projecting is devonshire dispatched remarkably on
                  estimating. Side in so life past.
                </p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6 col-sm-6">
              <div className="service-card">
                <h5>Traditional food</h5>
                <p>
                  Excellence projecting is devonshire dispatched remarkably on
                  estimating. Side in so life past.
                </p>
                <span>
                  <img src={serviceicon04} alt="serviceicon04" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="choose-sec tp-space pb-5">
        <div className="container">
          <div className="row justify-content-between align-items-center">
            <div className="col-lg-4">
              <div className="choose-img">
                <img
                  src={chooseimg01}
                  className="choose-img-01"
                  alt="chooseimg01"
                />
                <div className="choose-count">
                  <img src={yearicon} alt="" />
                  <h6>30+ years of experience</h6>
                </div>
                <img
                  src={chooseimg02}
                  className="choose-img-02"
                  alt="chooseimg02"
                />
              </div>
            </div>
            {/* <div className="col-lg-2"></div> */}
            <div className="col-lg-6">
              <div className="choose-content">
                <h6>About Us</h6>
                <h4>OUR COMMITMENT TO AUTHENTICITY & EXCELLENCE</h4>
                <p>
                  Every dish we create is a celebration of connection, crafted
                  with passion a inspired by diverse flavors. Join us in an
                  inviting space where every bite sparks joy and every moment
                  becomes a cherished memory.
                </p>
                <ul>
                  <li>Seasonal & Locally Sourced Ingredients</li>
                  <li>Vegetarian & Dietary-Friendly Options</li>
                  <li>Exquisite Pairings & Unique Flavors</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
<TrackingBar/>

        <MobileBottomBar />
      </section>
    </>
  );
}

export default Home;
